/**
 * GlossaryBlock - Term definitions with optional inline chips
 */

import React, { useState } from 'react'
import { GlossaryBlock as GlossaryBlockType } from '../../../../lib/cockpit/schemas'

interface GlossaryBlockProps {
  data: GlossaryBlockType['data']
  blockId: string
  compact?: boolean
}

function GlossaryBlock({ data, blockId, compact }: GlossaryBlockProps) {
  const [expandedTerm, setExpandedTerm] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const filteredTerms = searchQuery
    ? data.terms.filter((t: any) => 
        t.term.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.definition.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.aliases?.some((a: any) => a.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : data.terms

  if (compact || data.show_inline_chips) {
    return (
      <div className="flex flex-wrap gap-2">
        {data.terms.map((term: any) => (
          <button
            key={term.term}
            onClick={() => setExpandedTerm(expandedTerm === term.term ? null : term.term)}
            className={`
              px-3 py-1.5 text-sm rounded-full border transition-all
              ${expandedTerm === term.term
                ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-200'
                : 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }
            `}
          >
            {term.term}
          </button>
        ))}
        
        {expandedTerm && (
          <div className="w-full mt-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white">
                  {expandedTerm}
                </h4>
                {data.terms.find((t: any) => t.term === expandedTerm)?.aliases && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Also: {data.terms.find((t: any) => t.term === expandedTerm)?.aliases?.join(', ')}
                  </div>
                )}
              </div>
              <button
                onClick={() => setExpandedTerm(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="mt-2 text-gray-700 dark:text-gray-300 text-sm">
              {data.terms.find((t: any) => t.term === expandedTerm)?.definition}
            </p>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          placeholder="Search terms..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400"
        />
      </div>

      {/* Terms list */}
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {filteredTerms.map((term: any) => (
          <div key={term.term} className="py-3">
            <dt className="flex items-center gap-2">
              <span className="font-semibold text-gray-900 dark:text-white">
                {term.term}
              </span>
              {term.aliases && term.aliases.length > 0 && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  ({term.aliases.join(', ')})
                </span>
              )}
            </dt>
            <dd className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {term.definition}
            </dd>
            {term.related_terms && term.related_terms.length > 0 && (
              <div className="mt-2 flex items-center gap-1">
                <span className="text-xs text-gray-500 dark:text-gray-500">Related:</span>
                {term.related_terms.map((related: any) => (
                  <button
                    key={related}
                    onClick={() => setSearchQuery(related)}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {related}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredTerms.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
          No terms match your search.
        </p>
      )}
    </div>
  )
}

export default GlossaryBlock



